import {
  type ImageTransformPreset,
  imageTransformRuntimeOptions,
  imageTransformSizes,
  resizedAssetSrcSet,
  resizedAssetUrl,
  resizedAssetUrlFromValue,
} from "@content-types"
import { localAdditionalSettings } from "@data/localAdditionalSettings"
import { calculateLanguagePercentage, extractContent } from "@lib/helper"
import type { AdditionalSettings, ContentImage, SupportedLocale as Lang } from "@site-types/site"

export type GalleryImage = ContentImage & { asset?: { tags?: string[] } }

type ImageWithAsset =
  | {
      asset?: {
        objectKey?: string
        url?: string
        width?: number
        height?: number
      }
    }
  | undefined

export function imageUrl(image: ImageWithAsset, preset: ImageTransformPreset = "markdown") {
  const asset = image?.asset
  const imageOptions = imageTransformRuntimeOptions(import.meta.env)
  return (
    resizedAssetUrl(asset, preset, imageOptions) ??
    resizedAssetUrlFromValue(asset?.url, preset, imageOptions) ??
    asset?.url
  )
}

export function imageSrcSet(image: ImageWithAsset, preset: ImageTransformPreset = "markdown") {
  return resizedAssetSrcSet(image?.asset, preset, imageTransformRuntimeOptions(import.meta.env))
}

export function imageSizes(preset: ImageTransformPreset) {
  return imageTransformSizes(preset)
}

export function imageDimensions(image: ImageWithAsset) {
  const explicitWidth = image?.asset?.width
  const explicitHeight = image?.asset?.height
  if (Number.isFinite(explicitWidth) && Number.isFinite(explicitHeight)) {
    return { width: explicitWidth, height: explicitHeight }
  }

  const source = [image?.asset?.objectKey, image?.asset?.url].filter(Boolean).join(" ")
  const match = source.match(/-(\d+)x(\d+)-[a-z0-9]+(?:\s|\?|#|$)/i)
  if (!match) return {}

  return {
    width: Number(match[1]),
    height: Number(match[2]),
  }
}

export function localized<T extends { language?: string }>(items: T[], locale: Lang): T[] {
  return items.filter((item) => item.language === locale)
}

export function localizedSettings(data: { additionalSettings: AdditionalSettings[] }, locale: Lang) {
  return (
    localized<Record<string, unknown>>(localAdditionalSettings, locale)[0] ??
    localized<Record<string, unknown>>(data.additionalSettings, locale)[0] ??
    {}
  )
}

export function imageTitle(image: ContentImage | undefined, locale: Lang) {
  return extractContent(image?.asset?.title ?? "", locale)
}

export function imageAlt(image: ContentImage | undefined, locale: Lang) {
  return extractContent(image?.asset?.altText ?? "", locale)
}

export function tagsFor(
  images: Array<{ asset?: { tags?: string[] } } | undefined>,
  locale: Lang,
  options: { filterByLanguage?: boolean } = {}
) {
  const filterByLanguage = options.filterByLanguage ?? true

  return [
    ...new Set(
      images
        .flatMap((image) => image?.asset?.tags ?? [])
        .filter((tag) => tag && !tag.startsWith("PBPage"))
        .filter((tag) => {
          if (!filterByLanguage) return true
          const { englishPercentage, japanesePercentage } = calculateLanguagePercentage(tag)
          if (locale === "en" && japanesePercentage > 75) return false
          if (locale === "ja" && englishPercentage > 75) return false
          return true
        })
    ),
  ]
}
